import { CenteredLoader } from '@trackman/baseball-shared-components';
import { Button } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionType } from 'enums/SessionType';
import dayjs, { Dayjs } from 'dayjs';
import {
  IApiSessionTeam,
  IField,
  ILeague,
  ILevel,
  IOwningOrganization,
  ISearchTeam,
  ISessionUpdateRequest,
  IVenue,
} from 'interfaces/review/session/Session';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import Features from './components/Features/Features';
import Tabs from './components/Tabs/Tabs';
import TeamsAndSettings from './components/TeamsAndSettings/TeamsAndSettings';
import {
  Content,
  Controls,
  Divider,
  InfoNote,
  LoaderWrapper,
  MergeModalFooter,
  RefreshTime,
  RefreshWrapper,
  StyledButton,
  StyledInfoIcon,
  StyledModal,
  StyledRotateIcon,
  TabsWrapper,
} from './styles/EditSessionModalStyles';

interface IProps {
  visible: boolean;
  session?: IApiSessionMetadataUpdated;
  onDismissCallback: () => void;
  onApplyCallback: (update: ISessionUpdateRequest) => void;
  contextualDataLoading?: boolean;
}

const EditSessionModal: React.FC<IProps> = observer(({ visible, session, onDismissCallback, onApplyCallback, contextualDataLoading }) => {
  const [selectedLeague, setSelectedLeague] = useState<ILeague>({} as ILeague);
  const [selectedLevel, setSelectedLevel] = useState<ILevel>({} as ILevel);
  const [selectedField, setSelectedField] = useState<IField>({} as IField);
  const [selectedVenue, setSelectedVenue] = useState<IVenue>({} as IVenue);
  const [dailySequenceNumber, setDailySequenceNumber] = useState<number | undefined>(undefined);
  const [externalSessionId, setExternalSessionId] = useState<string>();
  const [selectedOrg, setSelectedOrg] = useState<IOwningOrganization>();
  const [owningOrganizations, setOwningOrganizations] = useState<IOwningOrganization[]>([]);
  const [selectedHomeTeam, setSelectedHomeTeam] = useState<ISearchTeam>();
  const [selectedAwayTeam, setSelectedAwayTeam] = useState<ISearchTeam>();
  const [invalidExternalId, setInvalidExternalId] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [areRostersLoading, setAreRostersLoading] = useState<boolean>(false);
  const emptyOrganization = { id: '', name: '---', shortName: '' };
  const [isAnyChange, setIsAnyChange] = useState<boolean>(false);
  const [lastRefresh, setLastRefresh] = useState<Dayjs | null>(null);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);

  const fetchRosters = async () => {
    if (areRostersLoading || !session) {
      return;
    }
    try {
      setAreRostersLoading(true);
      await contextualDataStore.syncAllAndGetLatestSync();

      setLastRefresh(dayjs());
    } finally {
      setAreRostersLoading(false);
    }
  };

  const getHomeTeam = () => {
    if (session && session.homeTeam?.id === selectedHomeTeam!.id) {
      return session.homeTeam;
    }

    return {
      id: selectedHomeTeam!.id,
      name: selectedHomeTeam!.name,
      shortName: selectedHomeTeam!.shortName,
      externalId: undefined,
      externalIds: [],
      lineup: [],
      organizationId: selectedOrg!.id,
    } as IApiSessionTeam;
  };

  const getAwayTeam = () => {
    if (session && session.awayTeam?.id === selectedAwayTeam!.id) {
      return session.awayTeam;
    }
    return {
      id: selectedAwayTeam!.id,
      name: selectedAwayTeam!.name,
      shortName: selectedAwayTeam!.shortName,
      externalId: undefined,
      externalIds: [],
      lineup: [],
      organizationId: selectedOrg!.id,
    } as IApiSessionTeam;
  };

  const isBp = session?.sessionType === SessionType.BattingPractice;
  const [isLoading, setIsLoading] = useState(false);

  const useNewSettingsHandler = async () => {
    if (session && externalSessionId && externalSessionId !== session.externalSessionId) {
      setIsLoading(true);
    }

    if (!session) {
      return;
    }

    if (!isBp && (!selectedAwayTeam || !selectedHomeTeam)) {
      return;
    }

    const updateRequest = {
      id: session.sessionId,
      owningOrganizationId: selectedOrg && selectedOrg.id !== '' ? selectedOrg.id : undefined,
      homeTeam: isBp ? session.homeTeam : getHomeTeam(),
      awayTeam: isBp ? session.awayTeam : getAwayTeam(),
      externalSessionId,
    } as ISessionUpdateRequest;

    onApplyCallback(updateRequest);
  };

  useEffect(() => {
    if (session) {
      setSelectedLeague(session.league as any);
      setExternalSessionId(session.externalSessionId);
      setSelectedLevel(session.level as any);
      setSelectedHomeTeam(session.homeTeam ? { ...session.homeTeam } : undefined);
      setSelectedAwayTeam(session.awayTeam ? { ...session.awayTeam } : undefined);
      setDailySequenceNumber(session.dailySequenceNumber || undefined);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      setSelectedOrg(owningOrganizations.find((o) => o.id === session.owningOrganizationId) || emptyOrganization);
    }
  }, [session, owningOrganizations]);

  useEffect(() => {
    if (session) {
      if (
        selectedLeague?.id !== session.league?.id ||
        externalSessionId !== session.externalSessionId ||
        selectedLevel?.id !== session.level?.id ||
        selectedHomeTeam?.id !== session.homeTeam?.id ||
        selectedAwayTeam?.id !== session.awayTeam?.id ||
        dailySequenceNumber !== session.dailySequenceNumber
      ) {
        setIsAnyChange(true);
      } else {
        setIsAnyChange(false);
      }
    }
  }, [
    selectedLeague,
    externalSessionId,
    selectedLevel,
    selectedField,
    selectedVenue,
    selectedHomeTeam,
    selectedAwayTeam,
    dailySequenceNumber,
  ]);

  return (
    <>
      {!session || !visible ? null : (
        <StyledModal open={visible} onClose={onDismissCallback} maxWidth='lg' title='Session settings'>
          {(isLoading || contextualDataLoading || contextualDataStore.isUpdating) && (
            <LoaderWrapper>
              <CenteredLoader size='large' />
            </LoaderWrapper>
          )}
          <Content>
            <TabsWrapper>
              <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
              {activeTab === 0 && (
                <RefreshWrapper>
                  {lastRefresh && (
                    <div>
                      <div>Last refreshed:</div>
                      <RefreshTime>{`${lastRefresh.format('DD/MM/YYYY')} @ ${lastRefresh.format('hh:mm')}`}</RefreshTime>
                    </div>
                  )}
                  <StyledButton
                    outlook='solid'
                    intent='primary'
                    label='Refresh rosters'
                    icon={() => <StyledRotateIcon />}
                    isDisabled={areRostersLoading}
                    onClick={fetchRosters}
                    width='156px'
                  />
                </RefreshWrapper>
              )}
            </TabsWrapper>
            {activeTab === 0 && (
              <TeamsAndSettings
                selectedLeague={selectedLeague}
                selectedLevel={selectedLevel}
                externalSessionId={externalSessionId}
                invalidExternalId={invalidExternalId}
                selectedOrg={selectedOrg}
                setExternalSessionId={setExternalSessionId}
                setInvalidExternalId={setInvalidExternalId}
                setSelectedAwayTeam={setSelectedAwayTeam}
                setSelectedHomeTeam={setSelectedHomeTeam}
                setSelectedOrg={setSelectedOrg}
                visible={visible}
                selectedAwayTeam={selectedAwayTeam}
                selectedHomeTeam={selectedHomeTeam}
                session={session}
              />
            )}
            {activeTab === 1 && <Features session={session} />}

            <Divider />
            <MergeModalFooter>
              {isAnyChange && (
                <InfoNote>
                  <StyledInfoIcon />
                  Changes are only finally saved when pressing “submit” on the main page.
                </InfoNote>
              )}
              <Controls>
                <Button intent='tertiary' label='Cancel' onClick={onDismissCallback} />
                <Button
                  intent='primary'
                  label='Save'
                  disabled={!isBp && (!selectedAwayTeam || !selectedHomeTeam || !selectedLeague || !selectedLevel)}
                  onClick={useNewSettingsHandler}
                />
              </Controls>
            </MergeModalFooter>
          </Content>
        </StyledModal>
      )}
    </>
  );
});

export default EditSessionModal;
